import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Bell, FileText, ShieldCheck, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "./api";
import { useAuth } from "./QcApp";

const STAGE_LABELS = {
  qc: "With QC",
  commercial: "With Commercial",
  unit_head: "With Unit Head",
  accounts: "With Accounts",
  closed: "Closed",
};

const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

export default function Home() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [myVendors, setMyVendors] = useState([]);
  const [, setTick] = useState(0);

  const load = () => api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const isQc = (user.roles || []).includes("qc") && !(user.roles || []).includes("admin");
  useEffect(() => {
    if (!isQc) return undefined;
    api.get("/vendors")
      .then((r) => setMyVendors(r.data.filter((v) => v.created_by === user.id)))
      .catch(() => {});
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const WINDOW_MS = 24 * 3600 * 1000;
  const activeWindows = myVendors
    .map((v) => ({ ...v, remaining: new Date(v.created_at).getTime() + WINDOW_MS - Date.now() }))
    .filter((v) => v.remaining > 0);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="space-y-10" data-testid="qc-home">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Sun Nutrafoods · Quality Control</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight md:text-4xl">
          Welcome, {user.name.split(" ")[0]}<span className="text-[#E5A93D]">.</span>
        </h1>
      </div>

      <div className="flex flex-wrap gap-6">
        <Link
          to="/qc/invoicing"
          data-testid="qc-tile-lab-invoicing"
          className="group block w-full rounded-2xl border border-[#E8E6E1] bg-white p-6 shadow-[0_8px_32px_rgba(28,63,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(28,63,43,0.12)] sm:w-[280px]"
        >
          <div className="flex items-start justify-between">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C3F2B]/[0.06]">
              <FileText className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <ArrowUpRight className="h-4 w-4 text-[#8A9A91] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#1C3F2B]" />
          </div>
          <h3 className="mt-5 font-display text-xl font-semibold">External Lab Invoicing</h3>
          <p className="mt-1.5 text-[13px] leading-relaxed text-[#8A9A91]">
            Export &amp; domestic invoicing for external lab payments.
          </p>
        </Link>
        <div
          data-testid="qc-tile-coming-soon"
          className="block w-full cursor-not-allowed rounded-2xl border border-[#E8E6E1] bg-white p-6 opacity-60 sm:w-[280px]"
        >
          <div className="flex items-start justify-between">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C3F2B]/[0.06]">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <span className="rounded-full bg-[#E5A93D] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#1C3F2B]">
              Coming Soon
            </span>
          </div>
          <h3 className="mt-5 font-display text-xl font-semibold">Certificate Compliance</h3>
          <p className="mt-1.5 text-[13px] leading-relaxed text-[#8A9A91]">Future module — space reserved.</p>
        </div>
      </div>

      {isQc && activeWindows.length > 0 && (
        <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6" data-testid="qc-vendor-window-card">
          <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold">
            <Timer className="h-4 w-4" /> Vendor edit window
          </h2>
          <ul className="space-y-2">
            {activeWindows.map((v) => (
              <li key={v.id} data-testid={`qc-vendor-window-${v.id}`} className="flex items-center justify-between rounded-xl border border-[#E8E6E1] px-4 py-2.5">
                <span className="text-sm font-medium">{v.name}</span>
                <span data-testid={`qc-vendor-window-timer-${v.id}`} className="font-mono text-sm font-semibold tabular-nums text-[#1C3F2B]">
                  {fmtCountdown(v.remaining)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-red-700" data-testid="qc-vendor-window-note">
            If you made any mistake you can correct before the times stops. After that request Accounts or Admin
          </p>
        </div>
      )}

      {dash && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
                <Bell className="h-4 w-4" /> Notifications
              </h2>
              <Button variant="ghost" size="sm" data-testid="qc-notif-read-all" onClick={markAllRead} className="text-xs text-[#8A9A91]">
                Mark all read
              </Button>
            </div>
            {dash.notifications.length === 0 ? (
              <p className="text-sm text-[#8A9A91]">Nothing new.</p>
            ) : (
              <ul className="space-y-3">
                {dash.notifications.slice(0, 8).map((n) => (
                  <li key={n.id} className={`text-sm leading-relaxed ${n.read ? "text-[#8A9A91]" : "font-medium text-[#1C3F2B]"}`}>
                    {n.case_id ? (
                      <Link to={`/qc/cases/${n.case_id}`} className="hover:underline">{n.message}</Link>
                    ) : (
                      n.message
                    )}
                    <span className="mt-0.5 block text-[11px] font-normal text-[#8A9A91]">
                      {new Date(n.created_at).toLocaleString("en-IN")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-6" data-testid="qc-stage-counts">
            <h2 className="mb-4 font-display text-lg font-semibold">Pipeline</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(dash.counts).map(([stage, count]) => (
                <div key={stage} className="rounded-xl border border-[#E8E6E1] px-4 py-3 text-center">
                  <p className="font-display text-2xl font-semibold">{count}</p>
                  <p className="text-[11px] uppercase tracking-wider text-[#8A9A91]">{STAGE_LABELS[stage]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
